/**
 * Cache identity for task-scoped reads.
 *
 * Typed factories rather than inline arrays so a query and every invalidation
 * that follows a mutation cannot drift apart (constitution §2.6).
 *
 * Only comments, moves and transcripts live here. Everything else the board
 * shows — tasks, their executions, their positions — arrives inside the
 * workspace aggregate and is keyed by `workspaceKeys.detail`. These three
 * deliberately do not: the aggregate is polled every two seconds for board
 * status and carries every task in the workspace, while their content is
 * unbounded and wanted only for the one thing someone has open.
 *
 * A transcript joined them in spec 081, which is the same rule one level
 * further down — it is wanted for the one *run* someone expanded, not the one
 * task. It used to arrive inside the aggregate, so every poll shipped every
 * run's entire activity record to a board that renders none of it.
 */
export const taskKeys = {
  comments: (workspaceId: string, taskId: string) =>
    ['task', taskId, 'comments', workspaceId] as const,
  moves: (workspaceId: string, taskId: string) =>
    ['task', taskId, 'moves', workspaceId] as const,
  // Who changed a run's subtasks (spec 148): read on the run page.
  activity: (workspaceId: string, taskId: string) =>
    ['task', taskId, 'activity', workspaceId] as const,
  // A mutation knows the subtask it touched, not the run it belongs to, so it
  // refreshes every run's activity in the workspace. The reads are lazy: only
  // a run page that is open refetches.
  activityInWorkspace: (workspaceId: string) =>
    ({
      predicate: (query: { queryKey: readonly unknown[] }) =>
        query.queryKey[0] === 'task' &&
        query.queryKey[2] === 'activity' &&
        query.queryKey[3] === workspaceId,
    }) as const,
  transcript: (workspaceId: string, taskId: string, executionId: string) =>
    ['task', taskId, 'transcript', executionId, workspaceId] as const,
  // What the task produced (spec 095): one entry per task, read on drawer open.
  results: (workspaceId: string, taskId: string) =>
    ['task', taskId, 'results', workspaceId] as const,
  // The files in the task's attachments folder (spec 120): read on drawer open.
  attachments: (workspaceId: string, taskId: string) =>
    ['task', taskId, 'attachments', workspaceId] as const,
  // What the task created on the forge (spec 119): read beside the results.
  artifacts: (workspaceId: string, taskId: string) =>
    ['task', taskId, 'artifacts', workspaceId] as const,
  // One run's Conversation (spec 149): read when the run is expanded, kept
  // current by the stream's `run_thread_changed`.
  thread: (workspaceId: string, taskId: string, executionId: string) =>
    ['task', taskId, 'thread', executionId, workspaceId] as const,
};

/**
 * Whether a cached query is the thread of one run, for the stream's
 * `run_thread_changed` (spec 149). A predicate rather than the key itself, so
 * the notifications feature can refresh a thread without knowing how its key is
 * built — the event names the run, not the task's key.
 */
export function isRunThreadQuery(
  queryKey: readonly unknown[],
  executionId: string,
): boolean {
  return (
    queryKey[0] === 'task' &&
    queryKey[2] === 'thread' &&
    queryKey[3] === executionId
  );
}
